import type {
  Credential,
  CredentialAuthType,
  CredentialStatus,
  OAuthDeviceStart,
  OAuthStartResponse,
  ProviderCatalogEntry,
  ProviderConnectionStatus,
} from '@/types/identity';
import type { BadgeVariant } from '@/components/ui';

export function credentialStatusLabel(status: CredentialStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'pending':
      return 'Pending';
    case 'expired':
      return 'Expired';
    case 'needs_reauth':
      return 'Needs reauth';
    case 'error':
      return 'Error';
  }
}

/**
 * Maps the identity-team `CredentialStatus` union to a Badge variant.
 * The shared `getStatusVariant` in `ui/Badge.tsx` doesn't know about
 * `needs_reauth` / `connected`, so we map locally rather than overload it.
 */
export function credentialStatusVariant(status: CredentialStatus): BadgeVariant {
  switch (status) {
    case 'connected':
      return 'success';
    case 'pending':
      return 'warning';
    case 'expired':
    case 'needs_reauth':
      return 'warning';
    case 'error':
      return 'error';
  }
}

/**
 * Pick the right provider-level badge from the credentials we actually
 * hold in the current scope. The backend's `ProviderSummary.status` is a
 * cross-tier aggregate that can disagree with what's listed for this tier
 * (e.g. backend says 'disconnected' while a connected credential exists in
 * the data we just rendered). Trust the local view — fall back to the
 * backend's status only when this scope has no credentials.
 */
export function deriveProviderStatus(
  credentials: Credential[],
  fallback: ProviderConnectionStatus,
): ProviderConnectionStatus {
  if (credentials.length === 0) return fallback;
  if (credentials.some((c) => c.status === 'connected')) return 'connected';
  if (credentials.some((c) => c.status === 'error')) return 'error';
  if (credentials.some((c) => c.status === 'needs_reauth' || c.status === 'expired')) {
    return 'needs_reauth';
  }
  // Everything else (pending only) — treat as needs_reauth so the user still
  // sees a non-success badge and follows up.
  return 'needs_reauth';
}

export function providerStatusLabel(status: ProviderConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'needs_reauth':
      return 'Needs reauth';
    case 'error':
      return 'Error';
    case 'disconnected':
      return 'Disconnected';
  }
}

export function providerStatusVariant(
  status: ProviderConnectionStatus,
): BadgeVariant {
  switch (status) {
    case 'connected':
      return 'success';
    case 'needs_reauth':
      return 'warning';
    case 'error':
      return 'error';
    case 'disconnected':
      return 'gray';
  }
}

/**
 * Short label for a credential's auth method, used to disambiguate a provider
 * that appears in more than one row (e.g. a Datadog `oauth2_manual` row and a
 * Datadog `api_key` row). `oauth2` is the interactive flow; `oauth2_manual` is
 * the pasted-token flow.
 */
export function authMethodLabel(authType: CredentialAuthType): string {
  switch (authType) {
    case 'oauth2':
      return 'OAuth';
    case 'oauth2_manual':
      return 'OAuth token';
    case 'api_key':
      return 'API token';
    case 'service_account':
      return 'Service account';
    case 'aws_role':
      return 'IAM role';
  }
}

/**
 * Whether a provider exposes a separate "app credential" (bot-token) flow that
 * is NOT one of its user-facing `supportedAuthTypes`. Slack is the driving case:
 * the *user* connection stays `['oauth2']`, but the org/space *bot* token is a
 * distinct api-key credential stored via the app / space-api-key endpoints. The
 * signal is a catalog entry that ships `apiKeySetupInstructions` (the required
 * bot scopes) while omitting `api_key` from `supportedAuthTypes` — a provider
 * that already lists `api_key` (e.g. Atlassian) renders that row normally and is
 * not an app-credential provider.
 */
export function hasAppCredentialFlow(
  provider: Pick<ProviderCatalogEntry, 'apiKeySetupInstructions' | 'supportedAuthTypes'>,
): boolean {
  return (
    !!provider.apiKeySetupInstructions &&
    Array.isArray(provider.supportedAuthTypes) &&
    !provider.supportedAuthTypes.includes('api_key')
  );
}

/**
 * Composite key identifying a single connectable row. The catalog now ships
 * one row per `(provider, authType, providerInstanceId)` — a provider can
 * appear more than once (e.g. Datadog via `oauth2_manual` and via `api_key`),
 * so keying on `provider.id` alone collides. `providerInstanceId` is folded in
 * so an instance-bound row and a provider-wide row stay distinct.
 */
export function providerRowKey(
  id: string,
  authType: string,
  providerInstanceId?: string,
): string {
  return `${id}::${authType}::${providerInstanceId ?? ''}`;
}

/**
 * Provider variants that should resolve to a different bundled logo slug than
 * their literal slugified form. The catalog ships a provider id (`aws-sso`)
 * and a verbose display name ("AWS IAM Identity Center"), neither of which
 * matches the brand-keyed asset (`aws`) — so without an alias both 404 and
 * fall back to a monogram. Keys are already-slugified values (from either the
 * id or the display name); values are the canonical `/logos/<slug>` slug.
 */
const LOGO_SLUG_ALIASES: Record<string, string> = {
  'aws-sso': 'aws',
  'aws-iam': 'aws',
  'aws-iam-identity-center': 'aws',
  'amazon-web-services': 'aws',
  // Google Workspace apps: the brand short-names slugify to forms that don't
  // match the bundled `google-*` assets (e.g. "Gmail" → `gmail`). The backend's
  // `{brand}-{product}` ids (e.g. `google-gmail`) resolve via the brand-prefix
  // cascade in ProviderLogo; these cover the bare short-names.
  gmail: 'google-gmail',
  gcal: 'google-calendar',
  gchat: 'google-chat',
};

function slugify(value: string | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Resolve a provider to the convention the bundled `/logos/<slug>-icon.svg`
 * set uses. Alias resolution checks the stable provider `id` first (it never
 * changes), then the display name; failing both, the display-name slug is
 * returned as the best-effort literal. `ProviderLogo` falls back to a monogram
 * if no asset matches, so an unmatched slug (or empty string) is harmless.
 */
export function deriveLogoSlug(
  provider: string | undefined,
  displayName: string | undefined,
): string {
  const idSlug = slugify(provider);
  const nameSlug = slugify(displayName);
  const aliased = LOGO_SLUG_ALIASES[idSlug] ?? LOGO_SLUG_ALIASES[nameSlug];
  return aliased ?? (nameSlug || idSlug);
}

/**
 * Format a positive time-until-expiry (in milliseconds) as a single coarse
 * unit, cascading down to the largest non-zero unit so we never render a
 * misleading "0d". Days → hours → minutes: a credential 3 hours from expiry
 * reads "3h", not "0d"; 40 minutes out reads "40m", not "0h"; under a minute
 * reads "<1m".
 *
 * Returns `null` when the timestamp is already in the past. Auto-refreshing
 * OAuth credentials (e.g. Datadog, which carries a `nextRefreshAt`) expose
 * the short-lived *access*-token `expiresAt`, which the backend rotates
 * server-side and is routinely stale while the credential stays connected.
 * Rendering "expires in <1m" for those is wrong — the caller should show
 * nothing rather than a fake countdown.
 */
export function formatExpiresIn(ms: number): string | null {
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}d`;
  if (hours >= 1) return `${hours}h`;
  if (minutes >= 1) return `${minutes}m`;
  return '<1m';
}

/**
 * Stash the pending OAuth target so the `/oauth/connected` landing page
 * can name it in its success copy. Mirrors the existing per-space
 * `openAuthorizationUrl` helper but keyed for identity flows so the two
 * don't collide if both happen in the same session.
 */
/**
 * Discriminate the device-authorization variant of an OAuth-start response.
 * `aws-sso` returns this instead of an `authorizationUrl`; callers must route
 * it to the device-flow modal rather than `openIdentityOAuthUrl` (which would
 * `window.open(undefined)` and pop a blank tab).
 */
export function isDeviceStart(r: OAuthStartResponse): r is OAuthDeviceStart {
  return (r as OAuthDeviceStart).flow === 'device';
}

export function openIdentityOAuthUrl(
  url: string,
  pending: { provider: string; credId?: string; scope: string },
): void {
  try {
    localStorage.setItem(
      'optr.identity.oauth.pending',
      JSON.stringify({ ...pending, ts: Date.now() }),
    );
  } catch {
    // Private browsing — non-fatal; landing page falls back to generic copy.
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
